import { enumMembers, type ApiSchema } from "../apiSchema";
import { requestJson, withQuery } from "../http/httpClient";
import {
  readArray,
  readBoolean,
  readNullableInteger,
  readNullableString,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "../http/jsonDecoding";
import { readStringList } from "./contentDecoders";
import { CHECK_STATUSES, ITEM_KINDS, readCheckResults, type CheckResult, type CheckStatus, type ItemKind } from "./reviewDecoders";

/**
 * The project ontology at one model version (FR-MODEL-1, `docs/components/content.md`
 * D-15): every term of its ontology modules, term search, and one term in detail. Each
 * answer names the model version it was read at; `commit` `null` reads the latest.
 */

export type TermKind = ApiSchema<"OntologyTermKind">;
export type RestrictionAxiom = ApiSchema<"RestrictionAxiom">;
export type RestrictionConstraint = ApiSchema<"RestrictionConstraint">;

export const TERM_KINDS = enumMembers<TermKind>({
  class: true,
  object_property: true,
  datatype_property: true,
  annotation_property: true,
  individual: true,
});
export const RESTRICTION_AXIOMS = enumMembers<RestrictionAxiom>({ subclass_of: true, equivalent_class: true });
export const RESTRICTION_CONSTRAINTS = enumMembers<RestrictionConstraint>({
  some_values_from: true,
  all_values_from: true,
  has_value: true,
  min_cardinality: true,
  max_cardinality: true,
  exact_cardinality: true,
});

export interface TermLabel {
  readonly text: string;
  readonly language: string | null;
}

/** What a term is, in short: enough to list it, place it in a tree and name it. */
export interface TermCard {
  readonly iri: string;
  readonly kind: TermKind;
  readonly labels: readonly TermLabel[];
  readonly definition: string | null;
  /** Named superclasses or superproperties. */
  readonly parents: readonly string[];
  readonly domain: readonly string[];
  readonly range: readonly string[];
  /** `VersionRef` of the first module declaring the term. */
  readonly module: string;
}

/** What a module extends: the domain model, or the DMW process ontology (a release of it, or
 * an extension). */
export type OntologyModuleGraph = ApiSchema<"CheckedGraph">;

const MODULE_GRAPHS = enumMembers<OntologyModuleGraph>({ model: true, process: true });

export interface OntologyModule {
  /** `VersionRef` `<item id>@<version>`. */
  readonly reference: string;
  readonly itemId: string;
  readonly name: string;
  readonly graph: OntologyModuleGraph;
  /** Whether it is the version's DMW process ontology, which its process checks read: its
   * library is the project's DMW basis. */
  readonly core: boolean;
  /** The pinned library it comes from, such as the DMW basis; `null` for the project's own. */
  readonly library: { readonly projectId: string; readonly version: string } | null;
}

/** A namespace the model's modules declare (their `@prefix`), a namespace with no term yet too. */
export interface DeclaredNamespace {
  readonly prefix: string;
  readonly namespace: string;
}

export interface OntologyTermListing {
  /** `CollectionVersionRef` of the model version read. */
  readonly version: string;
  readonly modules: readonly OntologyModule[];
  readonly terms: readonly TermCard[];
  /** By prefix; none from a server that doesn't serve them. */
  readonly namespaces: readonly DeclaredNamespace[];
}

/** One label or definition triple exactly as stored: what an edit replaces. */
export interface TermStatement {
  readonly predicate: string;
  readonly text: string;
  readonly language: string | null;
  /** `null` for plain and language strings. */
  readonly datatype: string | null;
  /** `VersionRef`s of the modules holding the triple. */
  readonly modules: readonly string[];
}

export interface TermRestriction {
  readonly axiom: RestrictionAxiom;
  readonly onProperty: string;
  readonly constraint: RestrictionConstraint;
  /** A class, individual or literal text; `null` for a nested class expression. */
  readonly filler: string | null;
  readonly fillerIsIri: boolean;
  readonly cardinality: number | null;
}

export interface TermUsage {
  readonly kind: ItemKind;
  readonly itemId: string;
  readonly name: string;
  readonly reference: string;
  /** For steps and Workflows: the pinned version that uses the term. */
  readonly via: string | null;
}

export interface TermDetails {
  readonly version: string;
  readonly term: TermCard;
  readonly declaredIn: readonly string[];
  readonly labels: readonly TermStatement[];
  readonly definitions: readonly TermStatement[];
  readonly children: readonly string[];
  readonly restrictions: readonly TermRestriction[];
  /** The term's own triples with the axioms they reach, as Turtle. */
  readonly turtle: string;
  readonly usage: readonly TermUsage[];
}

const ontologyPath = (projectId: string, read: "terms" | "search" | "term" | "checks"): `/${string}` =>
  `/projects/${encodeURIComponent(projectId)}/content/ontology/${read}`;

/** Every term of the modules the model version sees, its own and its pinned libraries' (the
 * closure's whole view, D-08); with `moduleIds`, of those modules only. */
export function listOntologyTerms(
  projectId: string,
  commit: string | null,
  moduleIds: readonly string[] | null,
  signal?: AbortSignal,
): Promise<OntologyTermListing> {
  return requestJson({
    method: "GET",
    path: withQuery(ontologyPath(projectId, "terms"), { version: commit, module: moduleIds }),
    signal,
    decodeResponse: readOntologyTermListing,
  });
}

/** The terms listing as module B serves it; `namespaces` is optional (b89), absent meaning none. */
export function readOntologyTermListing(body: unknown): OntologyTermListing {
  const listing = readObject(body, "ontology term listing");
  const namespaces = listing["namespaces"];
  return {
    version: readString(listing, "version"),
    modules: readArray(listing["modules"], "modules", (item) => {
      const module = readObject(item, "ontology module");
      const library = module["library"];
      return {
        reference: readString(module, "reference"),
        itemId: readString(module, "item_id"),
        name: readString(module, "name"),
        graph: readOneOf(module, "graph", MODULE_GRAPHS),
        core: readBoolean(module, "core"),
        library:
          library === null || library === undefined
            ? null
            : (() => {
                const pinned = readObject(library, "module library");
                return { projectId: readString(pinned, "project_id"), version: readString(pinned, "version") };
              })(),
      };
    }),
    terms: readArray(listing["terms"], "terms", readTermCard),
    namespaces:
      namespaces === undefined
        ? []
        : readArray(namespaces, "namespaces", (item) => {
            const declared = readObject(item, "declared namespace");
            return { prefix: readString(declared, "prefix"), namespace: readString(declared, "namespace") };
          }),
  };
}

/** Terms whose label or IRI contains `query`, best matches first. */
export function searchOntologyTerms(
  projectId: string,
  commit: string | null,
  query: string,
  signal?: AbortSignal,
): Promise<readonly TermCard[]> {
  return requestJson({
    method: "GET",
    path: withQuery(ontologyPath(projectId, "search"), { query, version: commit }),
    signal,
    decodeResponse: (body) => readArray(readObject(body, "term search result")["terms"], "terms", readTermCard),
  });
}

export function readOntologyTerm(
  projectId: string,
  commit: string | null,
  iri: string,
  signal?: AbortSignal,
): Promise<TermDetails> {
  return requestJson({
    method: "GET",
    path: withQuery(ontologyPath(projectId, "term"), { iri, version: commit }),
    signal,
    decodeResponse: (body) => {
      const details = readObject(body, "term details");
      return {
        version: readString(details, "version"),
        term: readTermCard(details["term"]),
        declaredIn: readStringList(details, "declared_in"),
        labels: readArray(details["labels"], "labels", readStatement),
        definitions: readArray(details["definitions"], "definitions", readStatement),
        children: readStringList(details, "children"),
        restrictions: readArray(details["restrictions"], "restrictions", readRestriction),
        turtle: readString(details, "turtle"),
        usage: readArray(details["usage"], "usage", readUsage),
      };
    },
  });
}

/** A label with its language tag, `null` without one. */
export function readTermLabel(value: unknown): TermLabel {
  const label = readObject(value, "label");
  return { text: readString(label, "text"), language: readNullableString(label, "language") };
}

export function readTermCard(value: unknown): TermCard {
  const card = readObject(value, "term card");
  return {
    iri: readString(card, "iri"),
    kind: readOneOf(card, "kind", TERM_KINDS),
    labels: readArray(card["labels"], "labels", readTermLabel),
    definition: readNullableString(card, "definition"),
    parents: readStringList(card, "parents"),
    domain: readStringList(card, "domain"),
    range: readStringList(card, "range"),
    module: readString(card, "module"),
  };
}

function readStatement(value: unknown): TermStatement {
  const statement: JsonObject = readObject(value, "term statement");
  return {
    predicate: readString(statement, "predicate"),
    text: readString(statement, "text"),
    language: readNullableString(statement, "language"),
    datatype: readNullableString(statement, "datatype"),
    modules: readStringList(statement, "modules"),
  };
}

function readRestriction(value: unknown): TermRestriction {
  const restriction = readObject(value, "restriction");
  return {
    axiom: readOneOf(restriction, "axiom", RESTRICTION_AXIOMS),
    onProperty: readString(restriction, "on_property"),
    constraint: readOneOf(restriction, "constraint", RESTRICTION_CONSTRAINTS),
    filler: readNullableString(restriction, "filler"),
    fillerIsIri: readBoolean(restriction, "filler_is_iri"),
    cardinality: readNullableInteger(restriction, "cardinality"),
  };
}

function readUsage(value: unknown): TermUsage {
  const usage = readObject(value, "term usage");
  return {
    kind: readOneOf(usage, "kind", ITEM_KINDS),
    itemId: readString(usage, "item_id"),
    name: readString(usage, "name"),
    reference: readString(usage, "reference"),
    via: readNullableString(usage, "via"),
  };
}

/** The checks of one model version (FR-MODEL-4): the model's shapes, competency questions and
 * consistency, as a change set's base is checked, and its process graph's beside them. */
export interface ModelVersionChecks {
  /** `<collection id>@<commit>`. */
  readonly version: string;
  /** Of the model's checks. */
  readonly status: CheckStatus;
  readonly engine: string;
  readonly results: readonly CheckResult[];
  readonly processStatus: CheckStatus;
  readonly processResults: readonly CheckResult[];
  /** The IRIs failing findings of both name as their focus, such as a term a shape rejects. */
  readonly failingFocuses: ReadonlySet<string>;
}

/** The checks of the model version; `commit` `null` checks the latest. The server keeps the
 * report of the few most recent versions, so a second look does not run them again. */
export function readModelVersionChecks(projectId: string, commit: string | null, signal?: AbortSignal): Promise<ModelVersionChecks> {
  return requestJson({
    method: "GET",
    path: withQuery(ontologyPath(projectId, "checks"), { version: commit }),
    signal,
    decodeResponse: (body) => {
      const report = readObject(body, "model version checks");
      const process = readObject(report["process"], "process checks");
      return {
        version: readString(report, "version"),
        status: readOneOf(report, "status", CHECK_STATUSES),
        engine: readString(report, "engine"),
        results: readCheckResults(report["results"]),
        processStatus: readOneOf(process, "status", CHECK_STATUSES),
        processResults: readCheckResults(process["results"]),
        failingFocuses: new Set([...failingFocuses(report["results"]), ...failingFocuses(process["results"])]),
      };
    },
  });
}

/** The IRIs failing results' findings focus on; `<iri>` as the engine writes them. */
function failingFocuses(value: unknown): string[] {
  return readArray(value, "check results", (item) => {
    const result = readObject(item, "check result");
    if (readString(result, "status") !== "fail") return [];
    return readArray(result["findings"], "findings", (finding) => readNullableString(readObject(finding, "finding"), "focus"));
  })
    .flat()
    .flatMap((focus) => (focus !== null && focus.startsWith("<") && focus.endsWith(">") ? [focus.slice(1, -1)] : []));
}
